import { ReactNode, useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button, LoadingGroup, LoadingSkeleton, Modal } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Heading } from "capitalrx-components-2.0/lib/typography";

import { SeparableAccumulationItem } from "@/App/api/member";
import { useAccumulations } from "@/App/hooks/accumulations";
import { AccumulatorCard } from "@/App/pages/PlanContributions/Accumulations/AccumulatorCard";
import { DisplayNetworkStatuses, DisplayScopes } from "@/App/types/accumulation";

export interface AccumControllerProps {
  readonly displayNetworkStatus: DisplayNetworkStatuses;
  readonly displayScope: DisplayScopes;
}
export const Accumulations = ({ displayNetworkStatus, displayScope }: AccumControllerProps) => {
  const { t: translate } = useTranslation();
  const {
    accumulations,
    isLoadingAccumulations,
    inNetworkAccumulations,
    outOfNetworkAccumulations,
    outOfPocketAccumulations,
    individualAccumulations,
    familyAccumulations,
    deductibleAccumulations,
    planMaximumAndFertilityAccumulations,
    customAccumulations,
  } = useAccumulations();

  const { columns } = useResponsive({
    columns: {
      mobile: 12,
      tablet: 6,
      desktop: 4,
    },
  });

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalContent, setModalContent] = useState<string | ReactNode>("");

  const accumulationsToDisplay = useMemo(() => {
    if (!isLoadingAccumulations) {
      if (displayScope === DisplayScopes.Individual) {
        if (displayNetworkStatus === DisplayNetworkStatuses.In) {
          return individualAccumulations(inNetworkAccumulations(accumulations.results));
        } else if (displayNetworkStatus === DisplayNetworkStatuses.Out) {
          return individualAccumulations(outOfNetworkAccumulations(accumulations.results));
        }
      } else if (displayScope === DisplayScopes.Family) {
        if (displayNetworkStatus === DisplayNetworkStatuses.In) {
          return familyAccumulations(inNetworkAccumulations(accumulations.results));
        } else if (displayNetworkStatus === DisplayNetworkStatuses.Out) {
          return familyAccumulations(outOfNetworkAccumulations(accumulations.results));
        }
      }
    }
  }, [
    inNetworkAccumulations,
    outOfNetworkAccumulations,
    individualAccumulations,
    familyAccumulations,
    displayNetworkStatus,
    displayScope,
    accumulations,
  ]);

  // Pharmacy-only = 1, Medical-only = 2, Both = 3
  const accumulationAppliesToPriority = (accum: SeparableAccumulationItem): number => {
    if (accum.applies_to.length == 2) return 3;
    if (accum.applies_to[0] == "medical") return 2;
    if (accum.applies_to[0] == "pharmacy") return 1;
    return null;
  }
  const accumulationsSortedByAppliesTo = (accums: SeparableAccumulationItem[]) => {
    return accums.sort((a, b) => accumulationAppliesToPriority(a) - accumulationAppliesToPriority(b));
  }

  const deductibleAccums = useMemo(() => {
    return deductibleAccumulations(accumulationsToDisplay);
  }, [accumulationsToDisplay, deductibleAccumulations]);

  const outOfPocketAccums = useMemo(() => {
    return outOfPocketAccumulations(accumulationsToDisplay);
  }, [accumulationsToDisplay, outOfPocketAccumulations]);

  const planMaxAndFertilityAccums = useMemo(() => {
    return planMaximumAndFertilityAccumulations(accumulationsToDisplay);
  }, [accumulationsToDisplay, planMaximumAndFertilityAccumulations]);

  const customAccums = useMemo(() => {
    return customAccumulations(accumulationsToDisplay);
  }, [accumulationsToDisplay, customAccumulations]);

  const modalCloseHandler = useCallback(() => {
    setShowModal(false);
  }, []);

  return (
    <>
      <Modal.Confirm visible={showModal} title={modalTitle} onClose={modalCloseHandler}>
        {modalContent}
      </Modal.Confirm>
      <LoadingGroup loading={isLoadingAccumulations}>
        <LoadingGroup.Placeholder
          fallback={
            <Stack>
              <LoadingSkeleton style={{ height: 200 }} />
              <LoadingSkeleton style={{ height: 200 }} />
              <LoadingSkeleton style={{ height: 200 }} />
            </Stack>
          }
        >
          <Stack spacing="large">
            {!!deductibleAccums?.length && (
              <Stack spacing="mini">
                <Shelf spacing="mini">
                  <Heading>{translate("BENEFITS.commercialBenefits.deductibles")}</Heading>
                  <Button
                    appearance="text"
                    icon="question-circle"
                    onClick={() => {
                      setModalTitle(translate("BENEFITS.commercialBenefits.deductibles"));
                      setModalContent(
                        <Trans
                          i18nKey="BENEFITS.commercialBenefits.deductiblesModalContent"
                          components={{
                            p: <p />,
                          }}
                        />,
                      );
                      setShowModal(true);
                    }}
                  />
                  <Shelf.Spacer />
                </Shelf>
                <Grid spacing="medium">
                  {accumulationsSortedByAppliesTo(deductibleAccums).map((accum) => {
                    return (
                      <Grid.Span columns={columns}>
                        <AccumulatorCard displayScope={displayScope} accumulator={accum} />
                      </Grid.Span>
                    );
                  })}
                </Grid>
              </Stack>
            )}
            {!!outOfPocketAccums?.length && (
              <Stack spacing="mini">
                <Shelf spacing="mini">
                  <Heading>{translate("BENEFITS.commercialBenefits.outOfPockets")}</Heading>
                  <Button
                    appearance="text"
                    icon="question-circle"
                    onClick={() => {
                      setModalTitle(translate("BENEFITS.commercialBenefits.outOfPockets"));
                      setModalContent(
                        <Trans
                          i18nKey="BENEFITS.commercialBenefits.outOfPocketsModalContent"
                          components={{
                            p: <p />,
                          }}
                        />,
                      );
                      setShowModal(true);
                    }}
                  />
                  <Shelf.Spacer />
                </Shelf>
                <Grid spacing="medium">
                  {accumulationsSortedByAppliesTo(outOfPocketAccums).map((accum) => {
                    return (
                      <Grid.Span columns={columns}>
                        <AccumulatorCard displayScope={displayScope} accumulator={accum} />
                      </Grid.Span>
                    );
                  })}
                </Grid>
              </Stack>
            )}
            {!!planMaxAndFertilityAccums?.length && (
              <Stack spacing="mini">
                <Shelf spacing="mini">
                  <Heading>{translate("BENEFITS.commercialBenefits.maximums")}</Heading>
                  <Button
                    appearance="text"
                    icon="question-circle"
                    onClick={() => {
                      setModalTitle(translate("BENEFITS.commercialBenefits.maximums"));
                      setModalContent(
                        <Trans
                          i18nKey="BENEFITS.commercialBenefits.maximumsModalContent"
                          components={{
                            p: <p />,
                          }}
                        />,
                      );
                      setShowModal(true);
                    }}
                  />
                  <Shelf.Spacer />
                </Shelf>
                <Grid spacing="medium">
                  {accumulationsSortedByAppliesTo(planMaxAndFertilityAccums).map((accum) => {
                    return (
                      <Grid.Span columns={columns}>
                        <AccumulatorCard displayScope={displayScope} accumulator={accum} />
                      </Grid.Span>
                    );
                  })}
                </Grid>
              </Stack>
            )}
            {!!customAccums?.length && (
              <Stack spacing="mini">
                <Shelf spacing="mini">
                  <Heading>{translate("BENEFITS.commercialBenefits.customAccumulations")}</Heading>
                  <Button
                    appearance="text"
                    icon="question-circle"
                    onClick={() => {
                      setModalTitle(translate("BENEFITS.commercialBenefits.customAccumulations"));
                      setModalContent(
                        <Trans
                          i18nKey="BENEFITS.commercialBenefits.customAccumulationsModalContent"
                          components={{
                            p: <p />,
                          }}
                        />,
                      );
                      setShowModal(true);
                    }}
                  />
                  <Shelf.Spacer />
                </Shelf>
                <Grid spacing="medium">
                  {accumulationsSortedByAppliesTo(customAccums).map((accum) => {
                    return (
                      <Grid.Span columns={columns}>
                        <AccumulatorCard displayScope={displayScope} accumulator={accum} />
                      </Grid.Span>
                    );
                  })}
                </Grid>
              </Stack>
            )}
          </Stack>
        </LoadingGroup.Placeholder>
      </LoadingGroup>
    </>
  );
};
